import React, { CSSProperties } from 'react';

import CommitIcon from '@atlaskit/icon/glyph/bitbucket/commits';
import PullRequestsIcon from '@atlaskit/icon/glyph/bitbucket/pullrequests';
import CommentIcon from '@atlaskit/icon/glyph/comment';
import EditIcon from '@atlaskit/icon/glyph/edit';
import ExportIcon from '@atlaskit/icon/glyph/export';
import PeopleIcon from '@atlaskit/icon/glyph/people';
import SubtaskIcon from '@atlaskit/icon/glyph/subtask';
import { Box } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import { ActivityOverviewEntry } from 'src/components/activity/types';

export function TimelineBox(props: {
  entry: ActivityOverviewEntry;
  entryIndex: number;
  entryCount: number;
}): JSX.Element {
  const heightAndTopForLine = ((): Pick<CSSProperties, 'height' | 'top'> => {
    const offsetFromTop = '30px';

    if (props.entryCount === 1) {
      // There's only one entry; we don't need to show the line
      return { height: 0, top: 0 };
    }

    if (props.entryIndex === 0) {
      // This is the first entry in the group. Make the line shorter and move
      // it away from the top so it looks like it starts in the middle of
      // this entry's icon.
      return { height: `calc(100% - ${offsetFromTop})`, top: offsetFromTop };
    }

    if (props.entryIndex === props.entryCount - 1) {
      // This is the last entry in the group. Make the line shorter so that
      // it looks like it ends in the middle of this entry's icon.
      return { height: offsetFromTop, top: 0 };
    }

    // This is neither the first nor the last entry. Make it the full height
    // of the row to make it look like one unbroken line.
    return { height: '100%', top: 0 };
  })();

  return (
    <Box style={{ position: 'relative' }}>
      {/* The line that runs through the entire list */}
      <div
        style={{
          position: 'absolute',
          width: '2px',
          left: '20px',
          backgroundColor: token(
            'color.background.accent.gray.subtler',
            colors.N30
          ),
          ...heightAndTopForLine,
        }}
      />
      {/* A circular container and the icon for this activity */}
      <div
        style={{
          position: 'absolute',
          top: '18px',
          left: '5px',
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          background: token('color.background.accent.gray.subtler', colors.N30),
          // Use flex layout to center the icon
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ActivityIcon entry={props.entry} />
      </div>
    </Box>
  );
}

function ActivityIcon({
  entry,
}: {
  entry: ActivityOverviewEntry;
}): JSX.Element {
  switch (entry.type) {
    case 'draft-change':
    case 'status-change':
      return (
        <PullRequestsIcon
          primaryColor={token('color.icon', colors.N80)}
          size="medium"
          label=""
        />
      );
    case 'approval':
    case 'changes-requested':
    case 'reviewers-added':
      return (
        <PeopleIcon
          primaryColor={token('color.icon', colors.N80)}
          size="medium"
          label=""
        />
      );
    case 'comment-start':
      return (
        <CommentIcon
          primaryColor={token('color.icon', colors.N80)}
          size="medium"
          label=""
        />
      );
    case 'title-change':
    case 'description-change':
      return (
        <EditIcon
          primaryColor={token('color.icon', colors.N80)}
          size="medium"
          label=""
        />
      );
    case 'tasks-created':
    case 'tasks-resolved':
      return (
        <SubtaskIcon
          primaryColor={token('color.icon', colors.N80)}
          size="medium"
          label=""
        />
      );
    case 'attachment-added':
      return (
        <ExportIcon
          primaryColor={token('color.icon', colors.N80)}
          size="medium"
          label=""
        />
      );
    case 'update':
      return (
        <CommitIcon
          primaryColor={token('color.icon', colors.N80)}
          size="medium"
          label=""
        />
      );
  }
}
